import { getUpgrade, UPGRADES, type UpgradeDef } from '../data/upgrades';
import type { ParkingLot } from '../models/ParkingLot';

export type PurchaseCheck =
  | { ok: true }
  | {
      ok: false;
      reason: 'unknown-upgrade' | 'already-owned' | 'missing-prerequisite' | 'level-too-low' | 'insufficient-funds';
    };

/** Aggregated passive effects from all upgrades a lot owns. */
export interface AggregateUpgradeEffects {
  paymentThroughput: number;
  security: number;
  demandMultiplier: number; // 1.0 = no bonus
  evEnabled: boolean;
  dynamicPricing: boolean;
  /** True if any owned upgrade is an automatic entry gate (barrier / LPR). */
  entryAutomation: boolean;
  maintenance: number; // total monthly maintenance from upgrades
}

/**
 * UpgradeEngine (spec sections 17, 34): validates purchases against the tech
 * tree and aggregates owned upgrades into passive effects other engines read.
 * Pure & data-driven.
 */
export const UpgradeEngine = {
  owns(lot: ParkingLot, upgradeId: string): boolean {
    return lot.upgrades.includes(upgradeId);
  },

  canPurchase(lot: ParkingLot, def: UpgradeDef, availableCash: number): PurchaseCheck {
    if (this.owns(lot, def.id)) return { ok: false, reason: 'already-owned' };
    if (lot.level < def.requiredLevel) {
      return { ok: false, reason: 'level-too-low' };
    }
    for (const prereq of def.prerequisites) {
      if (!this.owns(lot, prereq)) {
        return { ok: false, reason: 'missing-prerequisite' };
      }
    }
    if (availableCash < def.cost) {
      return { ok: false, reason: 'insufficient-funds' };
    }
    return { ok: true };
  },

  /** Combine effects of all owned upgrades. */
  aggregate(lot: ParkingLot): AggregateUpgradeEffects {
    const result: AggregateUpgradeEffects = {
      paymentThroughput: 0,
      security: 0,
      demandMultiplier: 1,
      evEnabled: false,
      dynamicPricing: false,
      entryAutomation: false,
      maintenance: 0,
    };

    for (const id of lot.upgrades) {
      const def = getUpgrade(id);
      if (!def) continue;
      const e = def.effects;
      result.paymentThroughput += e.paymentThroughput ?? 0;
      result.security += e.security ?? 0;
      result.demandMultiplier += e.demandBonus ?? 0;
      if (e.evEnabled) result.evEnabled = true;
      if (e.dynamicPricing) result.dynamicPricing = true;
      if (e.entryAutomation) result.entryAutomation = true;
      result.maintenance += def.maintenanceCost;
    }

    return result;
  },

  /** Ids the lot could purchase right now (prereqs met, not owned). */
  availableFor(lot: ParkingLot): string[] {
    return Object.values(UPGRADES)
      .filter(
        (def) =>
          !this.owns(lot, def.id) &&
          lot.level >= def.requiredLevel &&
          def.prerequisites.every((p) => this.owns(lot, p))
      )
      .map((def) => def.id);
  },
};
